import dayjs from "dayjs";

/**
 * Parse a single time to minutes from midnight.
 * Supports: "6:00 AM", "6:00AM", "6 AM", 24h "18:00", "18:30".
 */
export function parseTimeToMinutes(str) {
  if (str == null || String(str).trim() === "") return null;
  // normalise dot-separated format: "06.00AM" → "06:00AM"
  const s = String(str).trim().replace(/^(\d{1,2})\.(\d{2})\s*(AM|PM)/i, "$1:$2$3");

  let m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ap = m[3].toUpperCase();
    if (Number.isNaN(h) || Number.isNaN(min) || min > 59) return null;
    if (ap === "PM" && h !== 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    if (h < 0 || h > 23) return null;
    return h * 60 + min;
  }

  m = s.match(/^(\d{1,2})\s*(AM|PM)$/i);
  if (m) {
    let h = parseInt(m[1], 10);
    const ap = m[2].toUpperCase();
    if (Number.isNaN(h)) return null;
    if (ap === "PM" && h !== 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    if (h < 0 || h > 23) return null;
    return h * 60;
  }

  m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    const h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (Number.isNaN(h) || Number.isNaN(min) || h > 23 || min > 59) return null;
    return h * 60 + min;
  }

  return null;
}

export function parseTrainerSlotRange(slotLabel) {
  if (!slotLabel || typeof slotLabel !== "string") return null;
  const normalized = slotLabel.replace(/[–—]/g, "-").trim();
  if (!normalized || /^n\/?a$/i.test(normalized) || normalized === "-") return null;

  let parts = normalized.split(/\s*-\s*/).map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) {
    parts = normalized.split(/\s+to\s+/i).map((p) => p.trim()).filter(Boolean);
  }
  if (parts.length !== 2) return null;

  const startMin = parseTimeToMinutes(parts[0]);
  const endMin = parseTimeToMinutes(parts[1]);
  if (startMin == null || endMin == null) return null;
  const latestStartMin = endMin - 60;
  if (latestStartMin < startMin) return null;
  return { startMin, endMin, latestStartMin };
}

export function minutesFromDayjsTime(d) {
  if (!d || typeof d.isValid !== "function" || !d.isValid()) return null;
  return d.hour() * 60 + d.minute();
}

export function hasTrainerMorningSlot(slotPayload) {
  return !!parseTrainerSlotRange(slotPayload?.morning_slot);
}

/** True if API sent a non-empty evening slot string (even if parse fails — for UI hints). */
export function rawEveningSlotPresent(slotPayload) {
  const v = slotPayload?.evening_slot;
  if (v == null) return false;
  const s = String(v).trim();
  if (!s || /^n\/?a$/i.test(s) || s === "-") return false;
  return true;
}

export function hasTrainerEveningSlot(slotPayload) {
  return !!parseTrainerSlotRange(slotPayload?.evening_slot);
}

/** dayjs times on today's date for TimePicker min/max */
export function getSessionTimeBoundsForPickers(slotPayload, session) {
  if (!slotPayload || (session !== "morning" && session !== "evening")) return null;
  const label =
    session === "morning" ? slotPayload.morning_slot : slotPayload.evening_slot;
  const w = parseTrainerSlotRange(label);
  if (!w) return null;
  const base = dayjs().startOf("day");
  return {
    slotStart: base.add(w.startMin, "minute"),
    slotEnd: base.add(w.endMin, "minute"),
    maxStart: base.add(w.latestStartMin, "minute"),
  };
}

export function isPtStartTimeWithinSessionSlot(startDayjs, slotPayload, session) {
  if (!startDayjs || !slotPayload || (session !== "morning" && session !== "evening")) {
    return false;
  }
  const label =
    session === "morning" ? slotPayload.morning_slot : slotPayload.evening_slot;
  const w = parseTrainerSlotRange(label);
  if (!w) return false;
  const pick = minutesFromDayjsTime(startDayjs);
  if (pick == null) return false;
  return pick >= w.startMin && pick <= w.latestStartMin;
}

export function isPtEndTimeWithinSessionSlot(endDayjs, slotPayload, session) {
  if (!endDayjs || !slotPayload || (session !== "morning" && session !== "evening")) {
    return false;
  }
  const label =
    session === "morning" ? slotPayload.morning_slot : slotPayload.evening_slot;
  const w = parseTrainerSlotRange(label);
  if (!w) return false;
  const pick = minutesFromDayjsTime(endDayjs);
  if (pick == null) return false;
  return pick >= w.startMin && pick <= w.endMin;
}

function formatMinutesAs12h(totalMin) {
  let h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const ap = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  const mm = String(m).padStart(2, "0");
  return `${h}:${mm} ${ap}`;
}

/**
 * Given a previously-saved "HH:mm-HH:mm" slot string, figure out which of the
 * trainer's current morning/evening windows it falls in, so a renewal can
 * pre-fill the same session + start/end time instead of starting blank.
 * Returns { session, start: dayjs, end: dayjs } (times anchored to today) or null.
 */
export function resolveSessionFromSlotString(slotString, slotPayload) {
  const range = parseTrainerSlotRange(slotString);
  if (!range || !slotPayload) return null;
  const base = dayjs().startOf("day");
  for (const session of ["morning", "evening"]) {
    const label = session === "morning" ? slotPayload.morning_slot : slotPayload.evening_slot;
    const window = parseTrainerSlotRange(label);
    if (!window) continue;
    if (range.startMin >= window.startMin && range.startMin <= window.latestStartMin) {
      return {
        session,
        start: base.add(range.startMin, "minute"),
        end: base.add(range.endMin, "minute"),
      };
    }
  }
  return null;
}

export function trainerSlotAllowedStartSummaryForSession(slotPayload, session) {
  if (!slotPayload || (session !== "morning" && session !== "evening")) return "";
  const label =
    session === "morning" ? slotPayload.morning_slot : slotPayload.evening_slot;
  const w = parseTrainerSlotRange(label);
  if (!w) return "";
  return `Session window ${formatMinutesAs12h(w.startMin)} – ${formatMinutesAs12h(
    w.endMin
  )}. Start: ${formatMinutesAs12h(w.startMin)}–${formatMinutesAs12h(
    w.latestStartMin
  )}; end: ${formatMinutesAs12h(w.startMin)}–${formatMinutesAs12h(w.endMin)}.`;
}
